import { createTheme } from '@mui/material/styles';

const brandColors = {
  navy: '#0C2340',
  navyDeep: '#061527',
  navyLight: '#183B63',
  gold: '#D4AF37',
  goldLight: '#E2C56A',
  goldDeep: '#B48A20',
  cream: '#F8F5EC',
  creamSoft: '#F1EBDD',
  slate: '#5B6778',
  ink: '#14213D',
};

const theme = createTheme({
  palette: {
    primary: {
      main: brandColors.navy,
      light: brandColors.navyLight,
      dark: brandColors.navyDeep,
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColors.gold,
      light: brandColors.goldLight,
      dark: brandColors.goldDeep,
      contrastText: brandColors.navy,
    },
    accent: {
      main: brandColors.gold,
      light: brandColors.goldLight,
      dark: brandColors.goldDeep,
      contrastText: brandColors.navy,
    },
    success: {
      main: '#15803d',
      light: '#22c55e',
      dark: '#166534',
    },
    error: {
      main: '#d14343',
      light: '#ef4444',
      dark: '#b91c1c',
    },
    background: {
      default: brandColors.cream,
      paper: '#ffffff',
      light: brandColors.creamSoft,
    },
    text: {
      primary: brandColors.ink,
      secondary: brandColors.slate,
      muted: '#8e97a7',
    },
    divider: 'rgba(12, 35, 64, 0.14)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    subtitle2: {
      fontWeight: 500,
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(12,35,64,0.06), 0 1px 2px rgba(12,35,64,0.04)',
    '0 4px 6px rgba(12,35,64,0.05), 0 2px 4px rgba(12,35,64,0.04)',
    '0 10px 15px rgba(12,35,64,0.06), 0 4px 6px rgba(12,35,64,0.04)',
    '0 16px 24px rgba(12,35,64,0.06), 0 6px 12px rgba(12,35,64,0.04)',
    '0 20px 40px rgba(12,35,64,0.08)',
    '0 24px 48px rgba(12,35,64,0.1)',
    '0 30px 60px rgba(12,35,64,0.12)',
    '0 8px 32px rgba(12,35,64,0.15)',
    '0 12px 40px rgba(12,35,64,0.2)',
    '0 16px 48px rgba(12,35,64,0.25)',
    '0 4px 20px rgba(0,0,0,0.06)',
    '0 8px 30px rgba(0,0,0,0.08)',
    '0 12px 40px rgba(0,0,0,0.1)',
    '0 16px 50px rgba(0,0,0,0.12)',
    '0 20px 60px rgba(0,0,0,0.14)',
    '0 24px 70px rgba(0,0,0,0.16)',
    '0 28px 80px rgba(0,0,0,0.18)',
    '0 32px 90px rgba(0,0,0,0.2)',
    '0 36px 100px rgba(0,0,0,0.22)',
    '0 40px 110px rgba(0,0,0,0.24)',
    '0 44px 120px rgba(0,0,0,0.26)',
    '0 48px 130px rgba(0,0,0,0.28)',
    '0 52px 140px rgba(0,0,0,0.3)',
    '0 56px 150px rgba(0,0,0,0.32)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: 'smooth',
          backgroundColor: brandColors.cream,
          color: brandColors.ink,
        },
        '@keyframes fadeInUp': {
          '0%': { opacity: 0, transform: 'translateY(30px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-468px 0' },
          '100%': { backgroundPosition: '468px 0' },
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-10px)' },
        },
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          padding: '10px 24px',
          fontWeight: 700,
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${brandColors.navy} 0%, ${brandColors.navyLight} 100%)`,
          boxShadow: '0 8px 24px rgba(12, 35, 64, 0.24)',
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(135deg, ${brandColors.navyLight} 0%, ${brandColors.navy} 100%)`,
            boxShadow: '0 12px 30px rgba(12, 35, 64, 0.32)',
          },
        },
        outlined: {
          borderWidth: 1.8,
          borderColor: brandColors.navy,
          color: brandColors.navy,
          '&:hover': {
            borderWidth: 1.8,
            borderColor: brandColors.navyLight,
            backgroundColor: 'rgba(12, 35, 64, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 28px rgba(12,35,64,0.08)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 16px 48px rgba(12,35,64,0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: brandColors.navy,
          fontWeight: 600,
          '&:hover': {
            color: brandColors.goldDeep,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(12, 35, 64, 0.12)',
        },
      },
    },
  },
});

export default theme;